import { apiFetch } from "./client";

/**
 * Kontrauzorci: šta je od robe zadržano i do kada se drži.
 *
 * Kontrauzorak je ono na čemu se radi superanaliza. Kad kupac tvrdi da roba nije ono što je nalaz
 * rekao, spor se rešava ponovnim merenjem istog materijala — i samo dok tegla još postoji. Zato je
 * ekran okrenut jednom pitanju: šta još stoji, i čemu rok ističe.
 *
 * Dva roka, dva odgovora. KK32 daje šest meseci za sirovinu, pa uzorak sa isporuke ili prijemne šarže
 * dobija šest meseci od dana uzimanja ako se ne unese drugi dan. Isto pitanje za gotov proizvod (KK52)
 * nije odgovoreno, pa uzorak sa LOT-a gotove robe ostaje bez roka — pogođen rok se na ekranu ne
 * razlikuje od poznatog, a po njemu bi neko bacio teglu.
 */

/** Gde uzorak stoji u odnosu na svoj rok. Računa se iz roka i današnjeg dana, ne pamti se. */
export const RETAINED_STATES = ["KEPT", "DUE_SOON", "EXPIRED", "NO_DEADLINE"] as const;
export type RetainedState = (typeof RETAINED_STATES)[number];

/** Na čemu uzorak visi. Baza dopušta tačno jednu vezu, pa je i ekran bira kao jednu. */
export const RETAINED_SUBJECTS = ["DELIVERY", "RECEIVING_LOT", "GOODS_LOT"] as const;
export type RetainedSubject = (typeof RETAINED_SUBJECTS)[number];

export type RetainedSample = {
  id: string;
  label: string;
  takenOn: string;
  keepUntil: string | null;
  state: RetainedState;
  /** Negativan kad je rok prošao — tako ekran kaže „11 dana prekoračeno" bez svog računa datuma. */
  daysLeft: number | null;
  location: string | null;
  note: string | null;
  subject: RetainedSubject;
  deliveryId: string | null;
  receivingLotId: string | null;
  goodsLotId: string | null;
  /** Isporuka, šarža ili LOT ispisani rečima — oznaka na ekranu ništa ne znači sama. */
  subjectLabel: string | null;
  companyId: string | null;
  recordedAt: string;
};

/** Brojevi se računaju nad celim skladištem, ne nad filtriranim spiskom. */
export type RetainedTally = {
  total: number;
  expired: number;
  dueSoon: number;
  withoutDeadline: number;
};

/**
 * Unos uzorka.
 *
 * Tačno jedna veza. `keepUntil` se sme izostaviti: server tada računa šest meseci od `takenOn` za
 * sirovinu, a za gotov proizvod ostavlja prazno.
 *
 * `companyId` namerno ne postoji — kog dobavljača uzorak tiče izlazi iz onoga sa čega je uzet.
 */
export type CreateRetainedSampleBody = {
  label: string;
  takenOn: string;
  keepUntil?: string | null;
  location?: string | null;
  note?: string | null;
  deliveryId?: string | null;
  receivingLotId?: string | null;
  goodsLotId?: string | null;
};

/**
 * Predmet se ne menja: uzorak upisan na pogrešnu isporuku je pogrešan uzorak, ne pogrešno polje.
 *
 * Polje koje se ne pošalje ostaje kako je bilo. Zato se rok briše preko `clearKeepUntil`, a ne praznim
 * datumom: skidanje roka je druga radnja od nedirania roka — uzorak bez roka nema dan za bacanje, pa se
 * to traži izričito.
 */
export type UpdateRetainedSampleBody = {
  label?: string | null;
  takenOn?: string | null;
  keepUntil?: string | null;
  clearKeepUntil?: boolean;
  location?: string | null;
  note?: string | null;
};

function query(f: Record<string, string | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v) p.set(k, v);
  const s = p.toString();
  return s ? `?${s}` : "";
}

export const retainedSampleApi = {
  search: (f: {
    state?: string;
    deliveryId?: string;
    receivingLotId?: string;
    goodsLotId?: string;
    takenFrom?: string;
    takenTo?: string;
    q?: string;
  }) => apiFetch<RetainedSample[]>(`/quality/retained-samples${query(f)}`),
  tally: () => apiFetch<RetainedTally>("/quality/retained-samples/tally"),
  create: (body: CreateRetainedSampleBody) =>
    apiFetch<RetainedSample>("/quality/retained-samples", { method: "POST", body }),
  update: (id: string, body: UpdateRetainedSampleBody) =>
    apiFetch<RetainedSample>(`/quality/retained-samples/${id}`, { method: "PUT", body }),
  discard: (id: string) => apiFetch<void>(`/quality/retained-samples/${id}`, { method: "DELETE" }),
};
